import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, PlusCircle, ClipboardList, BarChart3, LogOut, Users, Phone, Contact, Settings } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { can, ROLES, roleBadgeClass } from "@/lib/permissions";

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = user?.role;
  const [shop, setShop] = useState({ phone: "" });

  useEffect(() => {
    api.get("/shop").then((r) => setShop(r.data)).catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navItems = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", show: true },
    { to: "/services", label: "Barang", icon: ClipboardList, testid: "nav-services", show: true },
    { to: "/intake", label: "Tambah", icon: PlusCircle, testid: "nav-intake", show: can.createService(role) },
    { to: "/customers", label: "Customer", icon: Contact, testid: "nav-customers", show: can.manageCustomers(role) },
    { to: "/reports", label: "Laporan", icon: BarChart3, testid: "nav-reports", show: can.viewReports(role) },
    { to: "/users", label: "Pengguna", icon: Users, testid: "nav-users", show: can.manageUsers(role) },
    { to: "/settings", label: "Toko", icon: Settings, testid: "nav-settings", show: can.manageUsers(role) },
  ].filter((n) => n.show);

  return (
    <div className="min-h-screen bg-slate-50 pb-24 md:pb-6">
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-200/80 no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-10 w-16 sm:h-11 sm:w-20 flex items-center justify-center">
              <img src="/logo-rtm.png" alt="Rizki Service" className="h-full w-full object-contain" />
            </div>
            <div>
              <div className="font-display font-bold text-slate-900 text-lg leading-tight">Rizki Service</div>
              <div className="text-[11px] sm:text-xs text-slate-600 font-medium leading-tight">PT Rizki Teknologi Maju</div>
              {shop.phone && (
                <a
                  href={`tel:${shop.phone.replace(/[^0-9+]/g, "")}`}
                  className="hidden sm:inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-800 mt-0.5"
                  data-testid="header-phone"
                >
                  <Phone className="w-2.5 h-2.5" /> {shop.phone}
                </a>
              )}
            </div>
          </div>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
                    isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                  }`
                }
              >
                <n.icon className="w-4 h-4" />
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div className="hidden sm:flex flex-col items-end">
              <div className="text-sm font-medium text-slate-800 leading-tight">{user?.name}</div>
              <span
                className={`mt-0.5 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${roleBadgeClass(role)}`}
                data-testid="header-role-badge"
              >
                {ROLES[role]?.label || role}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-button"
              className="gap-1"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Keluar</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 no-print">
        <div className={`grid`} style={{ gridTemplateColumns: `repeat(${navItems.length}, minmax(0, 1fr))` }}>
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`${n.testid}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-3 text-[11px] font-medium ${
                  isActive ? "text-slate-900" : "text-slate-500"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <n.icon className={`w-5 h-5 ${isActive ? "text-blue-600" : ""}`} />
                  {n.label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
